import React, { useState, useEffect } from 'react';
import { getContacts, createContact, archiveContact, blockContact } from '../api/contacts';
import { HiOutlinePlus, HiOutlineMagnifyingGlass, HiOutlineXMark } from 'react-icons/hi2';
import toast from 'react-hot-toast';

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [pagination, setPagination] = useState({});
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('active');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', mobile: '', email: '', company_name: '', tags: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, page]);

  const load = async () => {
    try {
      const res = await getContacts({ search: search || undefined, status, page, limit: 20 });
      setContacts(res.data.data.contacts);
      setPagination(res.data.data.pagination);
    } catch { } finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createContact(form);
      toast.success('Contact created');
      setShowModal(false);
      setForm({ name: '', mobile: '', email: '', company_name: '', tags: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create contact');
    }
  };

  const handleArchive = async (id) => {
    if (!window.confirm('Archive this contact?')) return;
    try { await archiveContact(id); toast.success('Contact archived'); load(); } catch { toast.error('Failed'); }
  };

  const handleBlock = async (id) => {
    if (!window.confirm('Block this contact?')) return;
    try { await blockContact(id); toast.success('Contact blocked'); load(); } catch { toast.error('Failed'); }
  };

  const statusBadge = (s) => {
    const colors = { active: 'bg-green-100 text-green-700', blocked: 'bg-red-100 text-red-700', archived: 'bg-gray-100 text-gray-600' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[s] || ''}`}>{s}</span>;
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h1 className="text-xl font-bold text-dark-800">Contacts</h1>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors">
          <HiOutlinePlus className="w-4 h-4" /> Add Contact
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <HiOutlineMagnifyingGlass className="absolute left-3 top-2.5 w-4 h-4 text-dark-400" />
          <input type="text" placeholder="Search contacts..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 bg-white border border-dark-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="px-3 py-2 bg-white border border-dark-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500">
          <option value="active">Active</option>
          <option value="blocked">Blocked</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      <div className="bg-white rounded-xl border border-dark-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-dark-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-dark-500">Name</th>
                <th className="text-left px-4 py-3 font-medium text-dark-500 hidden sm:table-cell">Mobile</th>
                <th className="text-left px-4 py-3 font-medium text-dark-500 hidden md:table-cell">Company</th>
                <th className="text-left px-4 py-3 font-medium text-dark-500">Status</th>
                <th className="text-right px-4 py-3 font-medium text-dark-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-100">
              {loading ? (
                <tr><td colSpan={5} className="text-center py-8 text-dark-400">Loading...</td></tr>
              ) : contacts.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-dark-400">No contacts found</td></tr>
              ) : contacts.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-dark-500 hidden sm:table-cell">{c.mobile}</td>
                  <td className="px-4 py-3 text-dark-500 hidden md:table-cell">{c.company_name || '-'}</td>
                  <td className="px-4 py-3">{statusBadge(c.status)}</td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {c.status === 'active' && (
                      <>
                        <button onClick={() => handleArchive(c.id)} className="text-xs text-dark-400 hover:text-dark-600">Archive</button>
                        <button onClick={() => handleBlock(c.id)} className="text-xs text-red-400 hover:text-red-600">Block</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination.totalPages > 1 && (
          <div className="flex justify-between items-center px-4 py-3 border-t border-dark-200 text-sm text-dark-400">
            <span>Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 border border-dark-200 rounded hover:bg-gray-50 disabled:opacity-50">Prev</button>
              <button disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1 border border-dark-200 rounded hover:bg-gray-50 disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
            <div className="flex justify-between items-center p-4 border-b border-dark-200">
              <h2 className="font-bold text-dark-800">New Contact</h2>
              <button onClick={() => setShowModal(false)}><HiOutlineXMark className="w-5 h-5 text-dark-400" /></button>
            </div>
            <form onSubmit={handleCreate} className="p-4 space-y-3">
              <input type="text" required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
              <input type="text" required placeholder="Mobile (+923001234567)" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
              <input type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
              <input type="text" placeholder="Company (optional)" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
              <input type="text" placeholder="Tags (optional)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
                className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
              <button type="submit" className="w-full bg-primary-500 text-white py-2 rounded-lg font-medium text-sm hover:bg-primary-600">Create Contact</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
